import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';

import type { PopupListItem } from 'webapp-types';

import eq from 'webapp/helpers/eq';
import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import BillingLimitationWarningComponent from 'webapp/components/billing/limitation-warning';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ScrollableTextComponent from 'webapp/components/scrollable-text';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type PopupList from 'webapp/models-custom/popup/popup-list';

type Item = PopupListItem<string>;

interface Args {
	item: Item;
	showTextProperty?: keyof Item;
	onTriggerAction: (item: Item) => void;
	typedFilter?: string;
	popup?: PopupList;
}

interface SearchableListItemComponentInterface<T> {
	Element: HTMLLIElement;
	Args: T;
}

export default class SearchableListItemComponent extends Component<
	SearchableListItemComponentInterface<Args>
> {
	// timer for delayed sublist opening
	private hoverTimeoutId: number | null = null;

	get itemText() {
		const showTextProperty = this.args.showTextProperty ?? 'text';
		return String((this.args.item[showTextProperty] as string) || '');
	}

	get showOnlyIcon() {
		return !!this.args.item.icon && !this.args.showTextProperty;
	}

	get showAvatarOrInitals() {
		return this.args.item.img !== undefined;
	}

	get class() {
		return this.args.item.class || '';
	}

	get hasSublist() {
		return !!this.args.item.sublistItems?.length && !this.isFeatureLimited;
	}

	get isFeatureLimited() {
		const extra = this.args.item.extra;
		return extra?.type === 'featureLimitation' && !!extra?.feature;
	}

	get tooltipText() {
		if (this.isDisabled && this.args.item.disabledTooltipText) {
			return this.args.item.disabledTooltipText;
		} else if (this.args.item.extra?.type === 'limitReached') {
			return this.args.item.extra?.text || '';
		} else if (this.showOnlyIcon) {
			return this.itemText;
		}
	}

	get showUsed() {
		return !!this.args.item.used || !!this.args.item.forceShowUsed;
	}

	get isDisabled() {
		return this.args.item.isDisabled || this.isFeatureLimited;
	}

	@action
	handleMouseEnter(event: MouseEvent): void {
		const targetElement = event.currentTarget as HTMLElement;

		// set up timer for opening new sublist if this item has one
		if (targetElement) {
			this.cancelHoverTimer();
			this.hoverTimeoutId = window.setTimeout(() => {
				if (this.args.popup) {
					// check if we should open a new sublist for this element
					// if true close existing children and open new sublist if this item has one
					if (this.args.popup.shouldOpenNewSublist(targetElement)) {
						this.args.popup.closeChild();
						if (this.hasSublist) {
							this.openSublist(targetElement);
						}
					}
				}
				this.hoverTimeoutId = null;
			}, 300);
		}
	}

	@action
	handleMouseLeave(): void {
		this.cancelHoverTimer();
	}

	@action
	noAction() {
		// do nothing
	}

	@action
	triggerAction(e: PointerEvent) {
		if (this.args.item.isLoading) {
			return;
		}

		if (this.hasSublist) {
			e.stopPropagation();
		} else if (!this.isDisabled) {
			this.args.onTriggerAction(this.args.item);
		}
	}

	openSublist(element: HTMLElement) {
		if (!this.args.popup) {
			return;
		}

		this.args.popup.openSublist(
			element,
			this.args.item.sublistItems || [],
			(item: Item) => {
				this.args.onTriggerAction(item);
			},
			this.args.item.sublistTitle,
		);
	}

	override willDestroy() {
		super.willDestroy();
		this.cancelHoverTimer();
	}

	private cancelHoverTimer(): void {
		if (this.hoverTimeoutId) {
			window.clearTimeout(this.hoverTimeoutId);
			this.hoverTimeoutId = null;
		}
	}
	<template>
		<li
			class={{joinClasses (array @item.class (if @item.isSelected "selected"))}}
			...attributes
		>
			<a
				role="button"
				href={{@item.href}}
				target={{if @item.href "_blank"}}
				rel={{if @item.href "noopener noreferrer"}}
				class={{joinClasses
					(array
						(if this.isDisabled "disabled")
						(if (eq @item.extra.type "featureLimitation") "has-limitation-warning")
					)
				}}
				{{on "click" this.triggerAction}}
				{{on "mouseenter" (if this.hasSublist this.handleMouseEnter this.noAction)}}
				{{on "mouseleave" (if this.hasSublist this.handleMouseLeave this.noAction)}}
			>
				<ScrollableTextComponent>
					{{#if @item.isLoading}}
						<span class="loader-svg icon"></span>
					{{else if @item.icon}}
						<CommonUiIconComponent
							@name={{@item.icon}}
							@size="md"
							@class={{this.class}}
							{{! Only pass fill when color is explicitly set, so icons with baked-in colors (e.g. VerifiedFill) render correctly }}
							@fill={{if @item.color @item.color}}
						/>
					{{/if}}
					{{#if this.showAvatarOrInitals}}
						<AvatarOrInitialsComponent
							@name={{this.itemText}}
							@avatar={{@item.img}}
							@color={{@item.color}}
						/>
					{{/if}}
					{{#unless this.showOnlyIcon}}
						<div class="text-container">
							{{highlightResult text=this.itemText match=@typedFilter}}
						</div>
					{{/unless}}
					{{#if @item.affixIcon}}
						<CommonUiIconComponent
							@name={{@item.affixIcon}}
							@size="sm"
							@class="affix"
						/>
					{{/if}}
					{{#if this.showUsed}}
						<span class="used-badge">{{@item.used}}</span>
					{{/if}}
					{{#if @item.hasNewTag}}
						<div class="new-feature-tag">New</div>
					{{/if}}
					{{#if @item.hasBetaTag}}
						<div class="beta-tag">Beta</div>
					{{/if}}
					{{#if @item.hasSoonTag}}
						<div class="soon-tag">Soon</div>
					{{/if}}
					{{#if this.isFeatureLimited}}
						<BillingLimitationWarningComponent
							@feature={{@item.extra.feature}}
							@trackingOrigin={{@item.extra.trackingOrigin}}
							@text={{@item.extra.text}}
						/>
					{{/if}}
					{{#if this.tooltipText}}
						<ToolTipComponent @text={{this.tooltipText}} />
					{{/if}}
				</ScrollableTextComponent>
				{{#unless this.showOnlyIcon}}
					<div class="label-container">
						{{#if @item.text3}}
							<span class="label">{{@item.text3}}</span>
						{{/if}}
						{{#if @item.extra.statusObject.isError}}
							<ToolTipWithIconComponent
								@text={{@item.extra.statusObject.statusTitleAndDescriptionWithAction}}
								@iconName="AlertFill"
								@maxWidth={{235}}
							/>
						{{/if}}
						{{#if this.hasSublist}}
							<CommonUiIconComponent
								@name="ChevronRight"
								@size="sm"
								@class="sublist-indicator"
							/>
						{{/if}}
					</div>
				{{/unless}}
			</a>
		</li>
	</template>
}
